/**
 * Native-only initialization. Safe to import on web — all calls are gated
 * behind isNativePlatform() and dynamic imports so Capacitor plugins are
 * never pulled into the web bundle at runtime.
 */
import { isNativePlatform } from "./platform";
import { primeRememberMeFromNativeStore } from "./auth/sessionPersistence";
import { setColdBootTelemetry } from "./debug/authTelemetry";
import { awaitPendingWrites, pendingWriteCount } from "./auth/pendingWritesGate";

const SUPABASE_AUTH_STORAGE_KEY = "sb-xhoqjcdsprhrrvbminxe-auth-token";

/**
 * Cold-boot auth telemetry (native only). Logs one line describing what
 * the persistent stores contain BEFORE AuthContext runs, so we can tell
 * within a single app reopen whether:
 *   - Preferences held the session (expected path after this fix),
 *   - only WebView localStorage held it (legacy / pre-migration),
 *   - or both are empty (session was actually wiped and user will be bounced).
 */
async function logNativeAuthTelemetry(): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const [prefSession, prefRemember] = await Promise.all([
      Preferences.get({ key: SUPABASE_AUTH_STORAGE_KEY }),
      Preferences.get({ key: "lr_persisted_session_v1" }),
    ]);
    let localSession: string | null = null;
    let localRemember: string | null = null;
    try { localSession = window.localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY); } catch {}
    try { localRemember = window.localStorage.getItem("lr_persisted_session_v1"); } catch {}
    const snapshot = {
      preferences_supabase_session_present: !!prefSession.value,
      preferences_remember_me_present: !!prefRemember.value,
      localstorage_supabase_session_present: !!localSession,
      localstorage_remember_me_present: !!localRemember,
      captured_at: Date.now(),
    };
    console.log("[native-auth-telemetry] cold-boot storage state:", JSON.stringify(snapshot));
    // Publish to the on-screen debug badge so the user can SEE this
    // without needing logcat access.
    setColdBootTelemetry(snapshot);
  } catch (e) {
    console.warn("[native-auth-telemetry] failed:", e);
  }
}

export async function initNative(): Promise<void> {
  if (!isNativePlatform()) return;

  // Prime Remember Me values from Preferences into localStorage BEFORE
  // React mounts / AuthContext boots, so the synchronous fallback read
  // path in AuthContext still works after Android has wiped the WebView
  // storage on force-close. The primary Supabase session already reads
  // directly from Preferences via the async storage adapter in
  // integrations/supabase/client.ts, so this is defense-in-depth.
  try {
    await primeRememberMeFromNativeStore();
  } catch (e) {
    console.warn("[native-init] Remember Me prime failed:", e);
  }

  // Fire-and-forget telemetry — never blocks startup.
  void logNativeAuthTelemetry();

  // Force-close persistence guard: when Android backgrounds/pauses the
  // app, drain any in-flight auth-storage writes before we hand control
  // back. This closes the token-rotation race — supabase-js may have
  // just received a rotated refresh token and be mid-way through
  // `Preferences.set`; if the OS kills the process before that commit
  // flushes, the next cold boot reads the OLD (already-consumed) token
  // and the server returns `refresh_token_not_found`. Bounded at 1.5s
  // so a pathological hang cannot block backgrounding indefinitely.
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("pause", async () => {
      const n = pendingWriteCount();
      if (n > 0) {
        console.log(`[native-init] app pause — draining ${n} pending auth-storage write(s)`);
        await awaitPendingWrites(1500);
      }
    });
  } catch (e) {
    console.warn("[native-init] App pause listener setup failed (non-fatal):", e);
  }

  try {
    const [{ StatusBar, Style }, { SplashScreen }] = await Promise.all([
      import("@capacitor/status-bar"),
      import("@capacitor/splash-screen"),
    ]);
    await StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    await StatusBar.setBackgroundColor({ color: "#02857E" }).catch(() => {});
    // Splash auto-hides per capacitor.config launchShowDuration; this is a safety net.
    setTimeout(() => {
      SplashScreen.hide().catch(() => {});
    }, 1400);
  } catch {
    // Plugins unavailable; ignore on web.
  }
}
